/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useCallback } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
} from '@apollo/react-hooks';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import type { NotificationAction } from '../graphql/__types__/CreateContentMentionNotificationAction.ts';

import type {
	useMentionNotificationMutationCreateContentMentionNotificationActionMutation as useMentionNotificationMutationCreateContentMentionNotificationActionMutation$data,
	useMentionNotificationMutationCreateContentMentionNotificationActionMutationVariables as useMentionNotificationMutationCreateContentMentionNotificationActionMutation$variables,
} from './__types__/useMentionNotificationMutationCreateContentMentionNotificationActionMutation.ts';

const useMentionNotificationMutationCreateContentMentionNotificationActionMutation = gql`
	mutation useMentionNotificationMutationCreateContentMentionNotificationActionMutation(
		$contentId: ID!
		$mentions: [ContentMention]!
	) {
		createContentMentionNotificationAction(input: { contentId: $contentId, mentions: $mentions }) {
			contentId
			success
			errors {
				message
			}
		}
	}
`;
type MentionNotificationMutationProps = {
	contentId: string;
	setError: React.Dispatch<React.SetStateAction<Error | null>>;
	showDontNotifyFailedFlag: () => void;
};

export type MentionNotificationType = {
	mentionLocalId: string;
	mentionedUserAccountId: string;
	notificationAction: NotificationAction;
};

type SendCreateMentionNotificationProps = {
	mentions: MentionNotificationType[];
};

type UseMentionNotificationMutationResult = {
	sendMentionNotification: (props: SendCreateMentionNotificationProps) => void;
	sendCancelMentionNotification: (props: SendCreateMentionNotificationProps) => void;
	contentMentionNotificationError?: ApolloErrorInterop;
};

export const useMentionNotificationMutation = ({
	contentId,
	setError,
	showDontNotifyFailedFlag,
}: MentionNotificationMutationProps): UseMentionNotificationMutationResult => {
	const [createContentMentionNotificationAction, { error: contentMentionNotificationError }] =
		useMutation<
			useMentionNotificationMutationCreateContentMentionNotificationActionMutation$data,
			useMentionNotificationMutationCreateContentMentionNotificationActionMutation$variables
		>(useMentionNotificationMutationCreateContentMentionNotificationActionMutation);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const sendMentionNotification = useCallback(
		({ mentions }: SendCreateMentionNotificationProps) => {
			const fireAnalyticsEvent = (action: 'succeeded' | 'failed') => {
				createAnalyticsEvent({
					type: 'sendOperationalEvent',
					data: {
						source: 'mentionsNotifications',
						action,
						actionSubject: 'mentionInputMutation',
						attributes: {
							mentions: mentions.map((mention) => ({
								mentionedUserId: mention.mentionedUserAccountId,
								mentionId: mention.mentionLocalId,
							})),
							contentId,
						},
					},
				}).fire();
			};

			// NCS draft syncs need to happen prior to mutation in order for
			// email content to show up. A 15 second buffer after user closes
			// the flag notification will give draft syncs a chance to happen.
			setTimeout(() => {
				// tells backend to send notificiation for the list of mention IDs
				createContentMentionNotificationAction({
					variables: {
						contentId,
						mentions,
					},
				})
					.then(() => {
						fireAnalyticsEvent('succeeded');
					})
					.catch(() => {
						//TODO: create new error flag that handles multiple names
						fireAnalyticsEvent('failed');
					});
			}, 15000);
		},
		[contentId, createAnalyticsEvent, createContentMentionNotificationAction],
	);

	const sendCancelMentionNotification = useCallback(
		({ mentions }: SendCreateMentionNotificationProps) => {
			createContentMentionNotificationAction({
				variables: {
					contentId,
					mentions,
				},
			}).catch((error) => {
				setError(error);
				showDontNotifyFailedFlag();
			});
		},

		// eslint-disable-next-line react-hooks/exhaustive-deps
		[contentId, createContentMentionNotificationAction],
	);

	return {
		sendMentionNotification,
		sendCancelMentionNotification,
		contentMentionNotificationError,
	};
};
